<template>
  <!-- ============================================== -->
  <!-- COMMON AUTH COMPONENTS (all channels)         -->
  <!-- ============================================== -->

  <LoginDialog
    v-model:show-auth-modal="showAuthModal"
    :referrer="referrer"
    @signup="startSignup"
    @logged-in="onLoggedIn"
  >
    <template #authOptions="{ onSessionResponse, loading }">
      <GoogleOauthButton
        v-if="isOauthEnabled"
        :is-validating="loading"
        @authenticated="onSessionResponse"
      />
    </template>
  </LoginDialog>

  <ChannelOnboardingDialog
    v-model:is-visible="isOnboardingVisible"
    v-model:step="onboardingStep"
    @finish="onChannelOnboardingFinished"
  />

  <SignupOverlay
    v-if="channelStore.channel && signUpState !== null"
    :channel-id="channelStore.channel.id"
    :validated-identifiers="signUpState.validatedIdentifiers"
    :auth-channel-type="signUpState.authChannelType"
    :session-token="signUpState.sessionToken"
    @success="onSignUpFinish"
  />

  <!-- ============================================== -->
  <!-- CAPABILITY MANAGERS                           -->
  <!-- ============================================== -->

  <WalletCapabilityManager
    v-if="isWalletEnabled"
    ref="walletManager"
    @wallet-connected="onWalletConnected"
    @tokens-acquired="onTokensAcquired"
  />

  <MembershipCapabilityManager
    v-if="isMembershipsEnabled"
    ref="membershipManager"
    @membership-purchased="onMembershipPurchased"
    @upgrade-cancelled="onUpgradeCancelled"
  />

  <ProductPurchaseCapabilityManager
    v-if="isProductsGatingEnabled"
    ref="productPurchaseManager"
    @product-purchased="onProductPurchased"
    @upgrade-cancelled="onUpgradeCancelled"
  />

  <PointsCapabilityManager
    v-if="isPointsGatingEnabled"
    ref="pointsManager"
    @points-earned="onPointsEarned"
    @upgrade-cancelled="onUpgradeCancelled"
  />

  <!-- ============================================== -->
  <!-- UPGRADE PATH CHOOSER (mixed gating)           -->
  <!-- ============================================== -->

  <UpgradePathChooserModal
    v-model="showUpgradePathChooser"
    :rule="pendingUpgradeRule"
    @select-path="onUpgradePathSelected"
    @close="onUpgradeChooserClosed"
  />

  <!-- ============================================== -->
  <!-- SUPERFAN PURCHASE FLOW                         -->
  <!-- ============================================== -->

  <SuperfanPurchaseFlow
    v-model="showSuperfanFlow"
    @success="onSuperfanPurchased"
  />

  <!-- ============================================== -->
  <!-- PURCHASE COMPLETION (payment_intent handling)  -->
  <!-- ============================================== -->

  <PurchaseCompletionHandler @purchase-completed="onPurchaseCompleted" />
</template>

<script setup lang="ts">
import { ref, watch, defineAsyncComponent } from "vue";
import { useChannelStore } from "@/stores/channel.store";
import { useUserStore, type UserStoreMe } from "@/stores/user.store";
import { useChannelCapabilities } from "@/composables/use-channel-capabilities";
import {
  ChannelEventType,
  useChannelEvents,
  type ShowAuthModalEvent,
  type ShowUpgradeEvent,
} from "@/composables/channel-events";
import { useTrackingStore } from "@/stores/tracking.store";
import {
  EventCategory,
  TrackEventEnum,
} from "@/services/tracking/tracking-types";
import { useReferralService } from "@/features/channel/services/use-referral-service";
import {
  ConversionType,
  type AuthChannelType,
  type User,
  type ValidatedUserIdentifier,
} from "@/generated/graphql";
import { ChannelOnboardingStep } from "@/components/channel/auth/common/onboarding/channel-onboarding-types";
import {
  getUpgradePaths,
  useCapabilityCheck,
} from "@/features/gating/upgrades/use-upgrade-paths";
import type { AuthorizationRuleForGating } from "@/features/gating/use-gating-permissions";
import { GatingStrategyType } from "@/features/gating/strategies/gating-strategy";
import { useGatingEvaluator } from "@/features/gating/evaluation/use-gating-evaluator";

// Common components
import SignupOverlay from "./common/registration/signup-overlay.vue";
import GoogleOauthButton from "./common/auth/google-oauth-button.vue";
import LoginDialog from "./common/login/login-dialog.vue";

// Lazy load components
const ChannelOnboardingDialog = defineAsyncComponent(
  () =>
    import("@/components/channel/auth/common/onboarding/channel-onboarding-dialog.vue"),
);

const UpgradePathChooserModal = defineAsyncComponent(
  () => import("@/features/gating/upgrades/upgrade-path-chooser-modal.vue"),
);

const SuperfanPurchaseFlow = defineAsyncComponent(
  () => import("@/components/channel/superfan/superfan-purchase-flow.vue"),
);

// Capability managers
import WalletCapabilityManager from "./capabilities/wallet-capability-manager.vue";
import MembershipCapabilityManager from "./capabilities/membership-capability-manager.vue";
import ProductPurchaseCapabilityManager from "./capabilities/product-purchase-capability-manager.vue";
import PointsCapabilityManager from "./capabilities/points-capability-manager.vue";

// Purchase completion
import PurchaseCompletionHandler from "@/components/checkout/purchase-completion-handler.vue";

const channelStore = useChannelStore();
const userStore = useUserStore();
const trackingService = useTrackingStore();
const { processReferralConversion } = useReferralService();
const {
  isOauthEnabled,
  isWalletEnabled,
  isMembershipsEnabled,
  canUseProductsGating: isProductsGatingEnabled,
  canUsePointsGating: isPointsGatingEnabled,
} = useChannelCapabilities();

// Refs to capability managers
const walletManager = ref<InstanceType<typeof WalletCapabilityManager> | null>(
  null,
);
const membershipManager = ref<InstanceType<
  typeof MembershipCapabilityManager
> | null>(null);
const productPurchaseManager = ref<InstanceType<
  typeof ProductPurchaseCapabilityManager
> | null>(null);
const pointsManager = ref<InstanceType<typeof PointsCapabilityManager> | null>(
  null,
);

// Common auth state
const showAuthModal = ref(false);
const isOnboardingVisible = ref(false);
const onboardingStep = ref(ChannelOnboardingStep.waitingForMembership);
const referrer = ref<User | undefined>(undefined);
const signUpState = ref<{
  validatedIdentifiers: ValidatedUserIdentifier[];
  authChannelType: AuthChannelType;
  /**
   * Captured from the verifyAuthentication JWT when an in-iframe login flow
   * transitioned to signup. Echoed back to the host SDK after signup via
   * `useAuthSession.handleSignupComplete`. Undefined when signup was
   * triggered by the SDK-pushed embed `SIGN_UP_WITH_IDENTIFIERS` event —
   * we don't echo a JWT the SDK just supplied.
   */
  sessionToken?: string;
} | null>(null);

// Upgrade path chooser state
const pendingUpgradeRule = ref<AuthorizationRuleForGating | null>(null);
const showUpgradePathChooser = ref(false);

// Superfan flow state
const showSuperfanFlow = ref(false);
const capabilityCheck = useCapabilityCheck();
const { getGatingTypesForRule } = useGatingEvaluator();

/**
 * Dispatch map: maps each GatingStrategyType to the function that starts its upgrade flow.
 * To add a new upgrade path, add an entry here — no branching logic changes needed.
 */
function getUpgradePathDispatch(): Record<
  GatingStrategyType,
  ((rule: AuthorizationRuleForGating) => void) | null
> {
  return {
    [GatingStrategyType.membership]: membershipManager.value
      ? (rule) => membershipManager.value!.showUpgradeFlow(rule)
      : null,
    [GatingStrategyType.token]: walletManager.value
      ? (rule) => walletManager.value!.showUpgradeFlow(rule)
      : null,
    [GatingStrategyType.points]: pointsManager.value
      ? (rule) => pointsManager.value!.showUpgradeFlow(rule)
      : null,
    [GatingStrategyType.product]: productPurchaseManager.value
      ? (rule) => productPurchaseManager.value!.showUpgradeFlow(rule)
      : null,
  };
}

function dispatchUpgradePath(
  type: GatingStrategyType,
  rule: AuthorizationRuleForGating,
) {
  const dispatch = getUpgradePathDispatch();
  const handler = dispatch[type];

  handler?.(rule);
}

/**
 * Dispatch map for a CHANNEL-WIDE upgrade path. There is no channel-level
 * authorization rule, so each path starts its rule-less entry:
 *   - membership → `showUpgradeFlow()` (no rule; preserves plan-082 proration —
 *     the membership manager mutates the existing subscription).
 *   - token      → `showDefaultTokenTopUp()` (1-token top-up of the channel
 *     default token; NOT `showUpgradeFlow(null)`, which leaves `requestedToken`
 *     null and shows the top-up Done screen with no purchase).
 * Points/products have no rule-less channel-wide entry and are no-ops.
 * Sibling of `getUpgradePathDispatch()` — same "add an entry, not a branch" shape.
 */
function getChannelWideUpgradePathDispatch(): Record<
  GatingStrategyType,
  (() => void) | null
> {
  return {
    [GatingStrategyType.membership]: membershipManager.value
      ? () => membershipManager.value!.showUpgradeFlow()
      : null,
    [GatingStrategyType.token]: walletManager.value
      ? () => walletManager.value!.showDefaultTokenTopUp()
      : null,
    [GatingStrategyType.points]: null,
    [GatingStrategyType.product]: null,
  };
}

function dispatchChannelWideUpgradePath(type: GatingStrategyType) {
  const dispatch = getChannelWideUpgradePathDispatch();
  const handler = dispatch[type];

  handler?.();
}

/**
 * Resolves available upgrade paths for `rule` (or every capability-available
 * path when `rule` is null — a channel-wide intent) and routes accordingly:
 * multiple paths open the chooser modal, a single path dispatches directly,
 * zero paths is a no-op. Shared by `handleAuthModalRequest`'s logged-in
 * branch and `handleUpgradeRequest`'s channel-wide default case.
 */
function resolveAndRouteUpgrade(rule: AuthorizationRuleForGating | null) {
  pendingUpgradeRule.value = rule;

  const { hasMultiplePaths, singlePath } = getUpgradePaths(
    rule,
    capabilityCheck,
    rule ? getGatingTypesForRule(rule) : [],
  );

  if (hasMultiplePaths) {
    showUpgradePathChooser.value = true;
  } else if (singlePath) {
    if (rule) {
      dispatchUpgradePath(singlePath.type, rule);
    } else {
      dispatchChannelWideUpgradePath(singlePath.type);
    }
    pendingUpgradeRule.value = null;
  } else {
    // 0 paths — nothing to upgrade to. Leave no dangling pending rule.
    pendingUpgradeRule.value = null;
  }
}

// Listen for channel events
const { emitChannelEvent, watchEventsEffect } = useChannelEvents();

watchEventsEffect((event) => {
  switch (event.type) {
    case ChannelEventType.SHOW_AUTH_MODAL:
      handleAuthModalRequest(event);
      break;
    case ChannelEventType.SIGN_UP_WITH_IDENTIFIERS:
      // Intentionally omit the third (sessionToken) arg: this branch fires
      // when the embed broker received an SDK-pushed JWT. Passing it to
      // startSignup would cause signup-overlay to echo the same JWT back
      // to the SDK via auth-modal:success, triggering a redundant
      // validateFanfestAuth call and an unintended welcome toast.
      startSignup(event.validatedIdentifiers, event.authChannelType);
      break;
    case ChannelEventType.SHOW_UPGRADE_MODAL:
      handleUpgradeRequest(event);
      break;
    case ChannelEventType.SHOW_DEFAULT_TOKEN_TOPUP_MODAL:
      // Channel-wide "Top up" hero entry. The wallet manager resolves the
      // channel default token itself; no token data travels on the bus.
      walletManager.value?.showDefaultTokenTopUp();
      break;
    case ChannelEventType.SHOW_SUPERFAN_MODAL:
      showSuperfanFlow.value = true;
      break;
    default:
      if (import.meta.env.DEV) {
        console.log("[ChannelAuthOrchestrator] Unhandled event: ", event);
      }
      break;
  }
});

function handleAuthModalRequest(event: ShowAuthModalEvent) {
  if (!userStore.isAuthenticated) {
    // User not logged in - show login
    showAuthModal.value = true;
    referrer.value = event.referrer;

    if (channelStore.gatingExperience) {
      trackingService.track({
        event: TrackEventEnum.LoginTriggeredByGatingEvent,
        category: EventCategory.Identity,
        metadata: {
          ...channelStore.gatingExperience,
        },
      });
    }
    return;
  }

  // User is logged in but needs upgrade — resolve available paths
  const rule = event.requestedAuthorizationRule as
    | AuthorizationRuleForGating
    | undefined;
  if (!rule) return;

  trackingService.track({
    event: TrackEventEnum.UpgradeTriggeredByGatingEvent,
    category: EventCategory.Identity,
    metadata: {
      ...channelStore.gatingExperience,
    },
  });

  resolveAndRouteUpgrade(rule);
}

function onUpgradePathSelected(type: GatingStrategyType) {
  showUpgradePathChooser.value = false;
  const rule = pendingUpgradeRule.value;
  if (rule) {
    dispatchUpgradePath(type, rule);
  } else {
    // Channel-wide intent: no rule, so route to the rule-less entry
    // (membership → showUpgradeFlow(); token → showDefaultTokenTopUp()).
    dispatchChannelWideUpgradePath(type);
  }
  pendingUpgradeRule.value = null;
}

function onUpgradeChooserClosed() {
  showUpgradePathChooser.value = false;
  pendingUpgradeRule.value = null;
  emitChannelEvent({
    type: ChannelEventType.UPGRADE_FINISHED,
    conversionType: ConversionType.Free,
  });
}

function handleUpgradeRequest(event: ShowUpgradeEvent) {
  switch (event.gatingStrategyType) {
    case GatingStrategyType.membership: {
      if (event.selectedUpgradeObjectId) {
        membershipManager.value?.showCheckoutFlow(
          event.selectedUpgradeObjectId,
        );
      } else {
        membershipManager.value?.showUpgradeFlow();
      }
      break;
    }
    case GatingStrategyType.token: {
      walletManager.value?.showUpgradeFlow(null);
      break;
    }
    case GatingStrategyType.product: {
      productPurchaseManager.value?.showUpgradeFlow(
        event.requestedAuthorizationRule as
          | AuthorizationRuleForGating
          | undefined,
      );
      break;
    }
    case GatingStrategyType.points: {
      pointsManager.value?.showUpgradeFlow(
        event.requestedAuthorizationRule as
          | AuthorizationRuleForGating
          | undefined,
      );
      break;
    }
    default: {
      // No explicit gating strategy: this is a channel-wide upgrade intent
      // (the profile card's single "Upgrade / Top up" button). The available
      // paths are resolved from channel CAPABILITIES alone (a null rule means
      // "all capability-available paths").
      const rule = event.requestedAuthorizationRule ?? null;
      resolveAndRouteUpgrade(rule);
      break;
    }
  }
}

// Auth event handlers
function onLoggedIn(user: UserStoreMe) {
  showAuthModal.value = false;
  emitChannelEvent({
    type: ChannelEventType.USER_IDENTIFIED,
    user,
  });
  emitChannelEvent({
    type: ChannelEventType.AUTHENTICATION_FINISHED,
    willUpgrade: false,
  });
}

function startSignup(
  validatedIdentifiers: ValidatedUserIdentifier[],
  authChannelType: AuthChannelType,
  sessionToken?: string,
) {
  if (!channelStore.channel) return;

  showAuthModal.value = false;
  signUpState.value = {
    validatedIdentifiers,
    authChannelType,
    sessionToken,
  };
}

function onSignUpFinish(user: UserStoreMe) {
  signUpState.value = null;
  isOnboardingVisible.value = true;

  emitChannelEvent({
    type: ChannelEventType.USER_IDENTIFIED,
    user,
  });

  if (channelStore.userHasToSetCommunities) {
    onboardingStep.value = channelStore.hasMultipleCommunities
      ? ChannelOnboardingStep.selectCommunities
      : ChannelOnboardingStep.welcomeToChannel;
  } else {
    onboardingStep.value = ChannelOnboardingStep.welcomeToChannel;
  }

  // Process free referral conversion after signup
  processReferralConversion(ConversionType.Free);
}

function onChannelOnboardingFinished() {
  isOnboardingVisible.value = false;
  emitChannelEvent({
    type: ChannelEventType.AUTHENTICATION_FINISHED,
    willUpgrade: false,
  });
}

// Capability manager event handlers
function onWalletConnected() {
  // Wallet connected - may need to sync balances
}

function onTokensAcquired() {
  // Tokens acquired - refresh gating state
  channelStore.refreshPermissions();
}

function onMembershipPurchased() {
  channelStore.refreshPermissions();
}

function onProductPurchased() {
  channelStore.refreshPermissions();
  channelStore.refreshUserInChannel();
}

function onPointsEarned() {
  channelStore.refreshPermissions();
}

function onPurchaseCompleted() {
  channelStore.refreshPermissions();
  channelStore.refreshUserInChannel();
}

function onSuperfanPurchased() {
  showSuperfanFlow.value = false;
  channelStore.refreshUserInChannel();
  channelStore.refreshPermissions();
}

function onUpgradeCancelled() {
  emitChannelEvent({
    type: ChannelEventType.UPGRADE_FINISHED,
    conversionType: ConversionType.Free,
  });
}

// Watch for community selection requirement
watch(
  () => channelStore.userHasToSetCommunities,
  (newVal) => {
    if (newVal && channelStore.channelCommunities.length > 1) {
      onboardingStep.value = ChannelOnboardingStep.selectCommunities;
      isOnboardingVisible.value = true;
    }
  },
);
</script>
